import { FC, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CheckCircle2,
  Check,
  ChevronDown,
  Package,
  Store,
  TriangleAlert,
  Search,
} from "lucide-react";
import { useLocationTab } from "src/hooks/UseLocationTab";
import {
  OMNICHANNEL_TAB_IDS,
  OMNICHANNEL_TAB_ITEMS,
  generateOmnichannelCustomers,
  generateOmnichannelOrders,
  generateOmnichannelProducts,
  getChannelDistribution,
  getCltvBuckets,
  getCustomerSegmentation,
  getFinancialSummary,
  getMonthlyFinancials,
  getOverviewKpis,
  getOverviewSalesTrend,
  getTaxBreakdown,
  paginateRows,
  type OmnichannelCustomer,
  type OmnichannelOrder,
  type OmnichannelProduct,
  type OmnichannelSource,
  type OmnichannelStatus,
} from "./OmnichannelAnalyticsTabsDashboard.data";
import { getDashboardKpiLucideIconComponent } from "src/utils/dashboardKpiLucideIcon";
import CustomChartTooltip from "../../../components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  DashboardKpiCard,
  DefinitionTooltip,
  compareDashboardValues,
  dashboardDataTable as dt,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardSectionHeader,
  TablePagePagination,
  toggleTableSort,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import { CHART_COLOR_PALETTE } from "src/components/charts/ChartPalette";
import "./OmnichannelAnalyticsTabsDashboard.scss";

const PIE_COLORS = CHART_COLOR_PALETTE;
const DAILY_LINE_COLORS = {
  shopify: CHART_COLOR_PALETTE[0],
  odoo: CHART_COLOR_PALETTE[3],
};
const PAGE_SIZE = 10;

const formatCurrency = (value: number) =>
  `PKR ${Math.round(value).toLocaleString()}`;

const formatCompactCurrency = (value: number) =>
  value >= 1000 ? `PKR ${(value / 1000).toFixed(1)}k` : `PKR ${value}`;

const formatCurrencyTooltipValue = (value: number) => formatCurrency(value);
const formatNumberTooltipValue = (value: number) => String(value);

const tableTotalPages = (totalRows: number) =>
  Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

type InventorySortKey =
  | "name"
  | "sku"
  | "category"
  | "shopifyStock"
  | "odooStock"
  | "totalStock"
  | "reorderPoint"
  | "status";
type CustomerSortKey = "name" | "type" | "totalOrders" | "totalSpent" | "cltv" | "lastOrder";

const TOP_PRODUCTS_TOOLTIP: DefinitionTooltipInfo = {
  description:
    "Shows the list of products with their current stock levels and average price.",
};

const parseKeyDirection = <K extends string>(value: string): { key: K; direction: SortDirection } => {
  const last = value.lastIndexOf("_");
  return {
    key: value.slice(0, last) as K,
    direction: value.slice(last + 1) as SortDirection,
  };
};

const OMNICHANNEL_CUSTOMER_SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "totalSpent_desc", label: "Total spent (high → low)" },
  { value: "totalSpent_asc", label: "Total spent (low → high)" },
  { value: "cltv_desc", label: "CLTV (high → low)" },
  { value: "cltv_asc", label: "CLTV (low → high)" },
  { value: "totalOrders_desc", label: "Orders (high → low)" },
  { value: "totalOrders_asc", label: "Orders (low → high)" },
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "name_desc", label: "Name (Z–A)" },
  { value: "type_asc", label: "Type (A–Z)" },
  { value: "type_desc", label: "Type (Z–A)" },
  { value: "lastOrder_desc", label: "Last order (newest)" },
  { value: "lastOrder_asc", label: "Last order (oldest)" },
];

const OMNICHANNEL_METRIC_TOOLTIPS: Record<string, DefinitionTooltipInfo> = {
  Revenue: {
    description: "Total revenue from completed orders across connected channels in the selected range.",
    formula: "Sum of completed order totals",
  },
  Orders: {
    description: "Completed orders across Shopify and Odoo in the selected period.",
    formula: "Completed orders",
  },
  Customers: {
    description: "Customers with at least one completed order in the combined dataset.",
    formula: "Distinct customers",
  },
  "Avg. Order": {
    description: "Average order value across completed orders.",
    formula: "Total revenue ÷ Completed orders",
  },
  Inventory: {
    description: "Estimated value of on-hand inventory using unit cost.",
    formula: "Sum of (stock × cost) per SKU",
  },
  "Avg. CLTV": {
    description: "Average predicted customer lifetime value in your cohort.",
    formula: "Mean of customer CLTV estimates",
  },
  "Total Products": {
    description: "Products in your connected catalogs.",
    formula: "Count of SKUs",
  },
  "In Stock": {
    description: "Products currently above their reorder threshold.",
    formula: "SKUs with status in stock",
  },
  "Low Stock": {
    description: "Products at or below reorder point and need replenishment.",
    formula: "SKUs flagged low stock",
  },
  "Inventory Value": {
    description: "Monetary value of inventory on hand.",
    formula: "Sum of stock × unit cost",
  },
  "Total Customers": {
    description: "All customers in the current analytics sample.",
    formula: "Distinct customer records",
  },
  "New Customers": {
    description: "Customers classified as new in the selected window.",
    formula: "Customers with first purchase in range",
  },
  "Avg. Order Value": {
    description: "Average basket size for customers in this view.",
    formula: "Mean of order totals",
  },
  "Net Profit": {
    description: "Revenue less cost of goods, taxes, expenses, and refunds.",
    formula: "Revenue − COGS − Taxes − Expenses − Refunds",
  },
  "Total Taxes": {
    description: "Tax collected on completed orders in the period.",
    formula: "Sum of order tax lines",
  },
  Refunds: {
    description: "Value of refunded orders in the period.",
    formula: "Sum of refund totals",
  },
  "Out of Stock": {
    description: "SKUs with zero on-hand availability.",
    formula: "Count of out-of-stock products",
  },
};

const MetricCard: FC<{
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
  icon?: FC<{ className?: string }>;
  changePercent?: number;
  comparisonLabel?: string;
  statusHint?: string;
}> = ({
  label,
  value,
  tone = "neutral",
  icon: Icon,
  changePercent,
  comparisonLabel = "vs last period",
  statusHint,
}) => {
  const tooltip = OMNICHANNEL_METRIC_TOOLTIPS[label];

  return (
    <DashboardKpiCard
      title={label}
      value={value}
      metricLabelForTooltip={label}
      tooltip={tooltip}
      icon={
        Icon ? (
          <Icon className="sales-product-analytics-dashboard__metric-icon-svg" />
        ) : undefined
      }
      changePercent={changePercent}
      comparisonLabel={comparisonLabel}
      description={statusHint ?? undefined}
      tone={tone}
    />
  );
};

const SourceBadge: FC<{ source: OmnichannelSource }> = ({ source }) => (
  <span
    className={`sales-product-analytics-dashboard__source-badge sales-product-analytics-dashboard__source-badge--${source}`}
  >
    <Store size={12} />
    <span>{source === "shopify" ? "Shopify" : "Odoo"}</span>
  </span>
);

const SelectMenu: FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_value: string) => void;
  className?: string;
}> = ({ value, options, onChange, className = "" }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <div
      ref={rootRef}
      className={`sales-product-analytics-dashboard__select-menu ${className}`.trim()}
    >
      <button
        type="button"
        className={`sales-product-analytics-dashboard__select-trigger ${open ? "sales-product-analytics-dashboard__select-trigger--open" : ""}`}
        onClick={() => setOpen(current => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.label}</span>
        <ChevronDown className="sales-product-analytics-dashboard__select-trigger-icon" />
      </button>
      {open ? (
        <div className="sales-product-analytics-dashboard__select-dropdown" role="listbox">
          {options.map(option => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`sales-product-analytics-dashboard__select-option ${active ? "sales-product-analytics-dashboard__select-option--active" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="sales-product-analytics-dashboard__select-option-check">
                  {active ? <Check size={16} /> : null}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const OverviewSection: FC<{
  orders: OmnichannelOrder[];
  customers: OmnichannelCustomer[];
  products: OmnichannelProduct[];
  dateRange: { startDate: string; endDate: string };
  setDateRange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, customers, products, dateRange, setDateRange }) => {
  const trendData = useMemo(() => getOverviewSalesTrend(60), []);
  const channelData = useMemo(() => getChannelDistribution(orders), [orders]);
  const summary = useMemo(() => getFinancialSummary(orders), [orders]);
  const kpis = useMemo(
    () => getOverviewKpis(orders, customers, products),
    [orders, customers, products]
  );
  const topProducts = useMemo(() => products.slice(0, 5), [products]);
  const segmentation = useMemo(() => getCustomerSegmentation(customers), [customers]);
  const newCustomers = segmentation.find(item => item.label === "New")?.value ?? 0;
  const returningCustomers =
    segmentation.find(item => item.label === "Returning")?.value ?? 0;
  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title="Dashboard Overview"
        subtitle="Unified view across Shopify and Odoo channels"
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={setDateRange}
          />
        )}
      />

      <div className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--overview">
        <MetricCard
          label="Revenue"
          value={formatCurrency(kpis.totalRevenue)}
          changePercent={12.5}
          comparisonLabel="vs last month"
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Revenue")}
        />
        <MetricCard
          label="Orders"
          value={String(kpis.totalOrders)}
          changePercent={8.2}
          comparisonLabel="vs last month"
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Orders")}
        />
        <MetricCard
          label="Customers"
          value={String(kpis.activeCustomers)}
          statusHint={`${newCustomers} new this week`}
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Customers")}
        />
        <MetricCard
          label="Avg. Order"
          value={formatCurrency(kpis.avgOrderValue)}
          changePercent={-2.1}
          comparisonLabel="vs last month"
          tone="negative"
          icon={getDashboardKpiLucideIconComponent("Avg. Order")}
        />
        <MetricCard
          label="Inventory"
          value={formatCompactCurrency(kpis.inventoryValue)}
          statusHint={`${products.filter(product => product.status === "low_stock").length} low stock`}
          tone="neutral"
          icon={getDashboardKpiLucideIconComponent("Inventory")}
        />
        <MetricCard
          label="Avg. CLTV"
          value={formatCurrency(kpis.avgCltv)}
          changePercent={6.8}
          comparisonLabel="vs last quarter"
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Avg. CLTV")}
        />
      </div>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">
            Sales Trend
          </h3>
          <div className="sales-product-analytics-dashboard__chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={6} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={value => `PKR ${Math.round(value / 1000)}k`}
                />
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                <Line
                  type="monotone"
                  dataKey="shopify"
                  stroke={DAILY_LINE_COLORS.shopify}
                  strokeWidth={2.5}
                  dot={false}
                  name="Shopify"
                />
                <Line
                  type="monotone"
                  dataKey="odoo"
                  stroke={DAILY_LINE_COLORS.odoo}
                  strokeWidth={2.5}
                  dot={false}
                  name="Odoo"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__chart-legend">
            <span className="sales-product-analytics-dashboard__chart-legend-item">
              <span
                className="sales-product-analytics-dashboard__chart-legend-swatch"
                style={{ color: DAILY_LINE_COLORS.shopify }}
              />
              <span>Shopify</span>
            </span>
            <span className="sales-product-analytics-dashboard__chart-legend-item">
              <span
                className="sales-product-analytics-dashboard__chart-legend-swatch"
                style={{ color: DAILY_LINE_COLORS.odoo }}
              />
              <span>Odoo</span>
            </span>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">Channel Distribution</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="label" innerRadius={48} outerRadius={82} paddingAngle={4}>
                  {channelData.map((item, index) => (
                    <Cell key={item.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {channelData.map((item, index) => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                  />
                  <span>{item.label}</span>
                </span>
                <span>{item.percentage}%</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="sales-product-analytics-dashboard__grid sales-product-analytics-dashboard__grid--three">
        <DashboardDataTablePanel
          as="section"
          className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--top-products"
        >
          <DashboardDataTablePanelHeader>
            <div className="sales-product-analytics-dashboard__title-with-tooltip">
              <h3 className="sales-product-analytics-dashboard__card-title">Top Products</h3>
              <DefinitionTooltip label="Top Products" info={TOP_PRODUCTS_TOOLTIP} />
            </div>
          </DashboardDataTablePanelHeader>
          <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
            <DashboardDataTableTable
              className={`${dt.tableCompact} sales-product-analytics-dashboard__table sales-product-analytics-dashboard__table--overview`}
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th className={dt.number}>Stock</th>
                  <th className={dt.number}>Price</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map(product => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td className={dt.number}>
                      <span className="sales-product-analytics-dashboard__pill">
                        {product.totalStock}
                      </span>
                    </td>
                    <td className={dt.number}>{formatCurrency(product.price)}</td>
                  </tr>
                ))}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
        </DashboardDataTablePanel>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">
            Customer Breakdown
          </h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={segmentation} dataKey="value" nameKey="label" innerRadius={38} outerRadius={70}>
                  {segmentation.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#3569db" : "#27c45a"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            <div className="sales-product-analytics-dashboard__legend-row sales-product-analytics-dashboard__legend-row--center">
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span
                  className="sales-product-analytics-dashboard__legend-dot"
                  style={{ backgroundColor: "#3569db" }}
                />
                <span>{`New (${newCustomers})`}</span>
              </span>
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span
                  className="sales-product-analytics-dashboard__legend-dot"
                  style={{ backgroundColor: "#27c45a" }}
                />
                <span>{`Returning (${returningCustomers})`}</span>
              </span>
            </div>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">Financial Summary</h3>
          <div className="sales-product-analytics-dashboard__summary-list">
            {[
              { label: "Revenue", value: summary.revenue, tone: "neutral" },
              { label: "Gross Profit", value: summary.grossProfit, tone: "positive" },
              { label: "Taxes", value: summary.taxes, tone: "warning" },
              { label: "Expenses", value: summary.expenses, tone: "muted" },
              { label: "Refunds", value: summary.refunds, tone: "negative" },
              { label: "Net Profit", value: summary.netProfit, tone: "positive" },
            ].map(item => (
              <div key={item.label} className="sales-product-analytics-dashboard__summary-row">
                <span>{item.label}</span>
                <span
                  className={`sales-product-analytics-dashboard__summary-value sales-product-analytics-dashboard__summary-value--${item.tone}`}
                >
                  {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const SalesSection: FC<{
  orders: OmnichannelOrder[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, dateRange, onDateRangeChange }) => {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<OmnichannelSource | "all">("all");
  const [statusFilter, setStatusFilter] = useState<OmnichannelStatus | "all">("all");
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders.filter(order => {
      if (
        query &&
        !order.customerName.toLowerCase().includes(query) &&
        !order.orderNumber.toLowerCase().includes(query)
      ) {
        return false;
      }
      if (sourceFilter !== "all" && order.source !== sourceFilter) {
        return false;
      }
      return statusFilter === "all" || order.status === statusFilter;
    });
  }, [orders, search, sourceFilter, statusFilter]);
  useEffect(() => {
    setPage(1);
  }, [search, sourceFilter, statusFilter]);
  const pagedRows = useMemo(() => paginateRows(filtered, page), [filtered, page]);
  const channelData = useMemo(() => getChannelDistribution(filtered), [filtered]);

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title="Sales Overview"
        subtitle="Track revenue and order performance across Shopify and Odoo channels."
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">Sales by Channel</h3>
        <p className="sales-product-analytics-dashboard__card-subtitle">
          Order revenue split across omnichannel storefronts.
        </p>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={channelData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis dataKey="label" tick={false} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={value => `PKR ${Math.round(value / 1000)}k`} />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="value" fill="#2c68ad" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--orders"
      >
        <DashboardDataTablePanelHeader>
          <h3>Orders ({filtered.length})</h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder="Search orders..."
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={sourceFilter}
              onChange={value => setSourceFilter(value as OmnichannelSource | "all")}
              options={[
                { value: "all", label: "All Sources" },
                { value: "shopify", label: "Shopify" },
                { value: "odoo", label: "Odoo" },
              ]}
            />
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={statusFilter}
              onChange={value => setStatusFilter(value as OmnichannelStatus | "all")}
              options={[
                { value: "all", label: "All Statuses" },
                { value: "completed", label: "Completed" },
                { value: "pending", label: "Pending" },
                { value: "refunded", label: "Refunded" },
                { value: "cancelled", label: "Cancelled" },
              ]}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Source</th>
                <th>Channel</th>
                <th className={dt.number}>Total</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(order => (
                <tr key={order.id}>
                  <td>{order.orderNumber}</td>
                  <td>{order.customerName}</td>
                  <td><SourceBadge source={order.source} /></td>
                  <td>{order.channel === "online" ? "Online Store" : "Physical Store"}</td>
                  <td className={dt.number}>{formatCurrency(order.total)}</td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${order.status}`}>{order.status.toLowerCase()}</span></td>
                  <td>{order.date}</td>
                  </tr>
                ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const InventorySection: FC<{
  products: OmnichannelProduct[];
}> = ({ products }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState<{ key: InventorySortKey; direction: SortDirection } | null>(
    { key: "totalStock", direction: "desc" }
  );
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter(product => {
      if (query && !product.name.toLowerCase().includes(query) && !product.sku.toLowerCase().includes(query)) {
        return false;
      }
      return statusFilter === "all" || product.status === statusFilter;
    });
  }, [products, search, statusFilter]);
  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }

    return [...filtered].sort((left, right) => {
      switch (sort.key) {
        case "name":
          return compareDashboardValues(left.name, right.name, sort.direction);
        case "sku":
          return compareDashboardValues(left.sku, right.sku, sort.direction);
        case "category":
          return compareDashboardValues(left.category, right.category, sort.direction);
        case "shopifyStock":
          return compareDashboardValues(left.shopifyStock, right.shopifyStock, sort.direction);
        case "odooStock":
          return compareDashboardValues(left.odooStock, right.odooStock, sort.direction);
        case "totalStock":
          return compareDashboardValues(left.totalStock, right.totalStock, sort.direction);
        case "reorderPoint":
          return compareDashboardValues(left.reorderPoint, right.reorderPoint, sort.direction);
        case "status":
          return compareDashboardValues(left.status, right.status, sort.direction);
        default:
          return 0;
      }
    });
  }, [filtered, sort]);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sort]);
  const pagedRows = useMemo(() => paginateRows(sorted, page), [sorted, page]);
  const inStock = products.filter(product => product.status === "in_stock").length;
  const lowStock = products.filter(product => product.status === "low_stock").length;
  const outOfStock = products.filter(product => product.status === "out_of_stock").length;
  const inventoryValue = products.reduce((sum, product) => sum + product.totalStock * product.cost, 0);
  const stockComparison = products.slice(0, 10).map(product => ({
    name: product.name.length > 16 ? `${product.name.slice(0, 16)}...` : product.name,
    Shopify: product.shopifyStock,
    Odoo: product.odooStock,
  }));

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title="Inventory Overview"
        subtitle="Compare stock, availability, and inventory value across both sources."
        sticky
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Total Products"
          value={String(products.length)}
          statusHint="Unified catalog count"
          icon={Package}
        />
        <MetricCard
          label="In Stock"
          value={String(inStock)}
          statusHint={`${Math.round((inStock / products.length) * 100)}% available`}
          tone="positive"
          icon={CheckCircle2}
        />
        <MetricCard
          label="Low Stock"
          value={String(lowStock)}
          statusHint="Needs reorder attention"
          tone="negative"
          icon={TriangleAlert}
        />
       
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">Stock Comparison</h3>
        <p className="sales-product-analytics-dashboard__card-subtitle">
          Comparative stock visibility across both connected sources.
        </p>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stockComparison} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10 }} />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="Shopify" fill={DAILY_LINE_COLORS.shopify} radius={[0, 6, 6, 0]} />
              <Bar dataKey="Odoo" fill={DAILY_LINE_COLORS.odoo} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--inventory"
      >
        <DashboardDataTablePanelHeader>
          <h3>
            Products
          </h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder="Search products..."
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--inventory"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All status" },
                { value: "in_stock", label: "In stock" },
                { value: "low_stock", label: "Low stock" },
                { value: "out_of_stock", label: "Out of stock" },
              ]}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "name"))}>
                    Product
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "sku"))}>
                    SKU
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "category"))}>
                    Category
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "shopifyStock"))}>
                    Shopify
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "odooStock"))}>
                    Odoo
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalStock"))}>
                    Total
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "reorderPoint"))}>
                    Reorder
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "status"))}>
                    Status
                  </DashboardDataTableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(product => (
                <tr key={product.id}>
                  <td>{product.name}</td>
                  <td>{product.sku}</td>
                  <td>{product.category}</td>
                  <td className={dt.number}>{product.shopifyStock}</td>
                  <td className={dt.number}>{product.odooStock}</td>
                  <td className={dt.number}>{product.totalStock}</td>
                  <td className={dt.number}>{product.reorderPoint}</td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${product.status}`}>{product.status.replace(/_/g, " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const CustomersSection: FC<{
  customers: OmnichannelCustomer[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ customers, dateRange, onDateRangeChange }) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "new" | "returning">("all");
  const [sort, setSort] = useState<{ key: CustomerSortKey; direction: SortDirection } | null>(
    { key: "totalSpent", direction: "desc" }
  );
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return customers.filter(customer => {
      if (typeFilter !== "all" && customer.type !== typeFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        customer.name.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query)
      );
    });
  }, [customers, search, typeFilter]);
  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }

    return [...filtered].sort((left, right) => {
      switch (sort.key) {
        case "name":
          return compareDashboardValues(left.name, right.name, sort.direction);
        case "type":
          return compareDashboardValues(left.type, right.type, sort.direction);
        case "totalOrders":
          return compareDashboardValues(left.totalOrders, right.totalOrders, sort.direction);
        case "totalSpent":
          return compareDashboardValues(left.totalSpent, right.totalSpent, sort.direction);
        case "cltv":
          return compareDashboardValues(left.cltv, right.cltv, sort.direction);
        case "lastOrder":
          return compareDashboardValues(left.lastOrder, right.lastOrder, sort.direction);
        default:
          return 0;
      }
    });
  }, [filtered, sort]);
  useEffect(() => {
    setPage(1);
  }, [search, sort, typeFilter]);
  const pagedRows = useMemo(() => paginateRows(sorted, page), [sorted, page]);
  const cltvBuckets = useMemo(() => getCltvBuckets(customers), [customers]);
  const segmentation = useMemo(() => getCustomerSegmentation(customers), [customers]);
  const newCustomers = segmentation.find(item => item.label === "New")?.value ?? 0;
  const returningCustomers =
    segmentation.find(item => item.label === "Returning")?.value ?? 0;
  const avgCltv = Math.round(customers.reduce((sum, customer) => sum + customer.cltv, 0) / customers.length);
  const avgOrderValue = Math.round(customers.reduce((sum, customer) => sum + customer.avgOrderValue, 0) / customers.length);

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title="Customers Overview"
        subtitle="Review customer growth, loyalty, and top buyers across channels."
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Total Customers"
          value={String(customers.length)}
          statusHint="Active Shopify and Odoo buyers"
          icon={getDashboardKpiLucideIconComponent("Total Customers")}
        />
        <MetricCard
          label="New Customers"
          value={String(segmentation[0].value)}
          statusHint="Recent acquisitions"
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("New Customers")}
        />
        <MetricCard
          label="Avg. CLTV"
          value={formatCurrency(avgCltv)}
          statusHint="Customer lifetime value"
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Avg. CLTV")}
        />
        <MetricCard
          label="Avg. Order Value"
          value={formatCurrency(avgOrderValue)}
          statusHint="Average basket size"
          icon={getDashboardKpiLucideIconComponent("Avg. Order Value")}
        />
      </div>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">CLTV Distribution</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cltvBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
                <XAxis dataKey="label" tick={false} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                <Bar dataKey="value" fill="#2c68ad" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">Customer Segmentation</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={segmentation} dataKey="value" nameKey="label" innerRadius={40} outerRadius={74}>
                  {segmentation.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#3569db" : "#27c45a"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            <div className="sales-product-analytics-dashboard__legend-row sales-product-analytics-dashboard__legend-row--center">
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span className="sales-product-analytics-dashboard__legend-dot" style={{ backgroundColor: "#3569db" }} />
                <span>{`New (${newCustomers})`}</span>
              </span>
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span className="sales-product-analytics-dashboard__legend-dot" style={{ backgroundColor: "#27c45a" }} />
                <span>{`Returning (${returningCustomers})`}</span>
              </span>
            </div>
          </div>
          </section>
      </div>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--customers"
      >
        <DashboardDataTablePanelHeader>
          <h3>Top Customers</h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder="Search customers..."
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={typeFilter}
              onChange={value => setTypeFilter(value as "all" | "new" | "returning")}
              options={[
                { value: "all", label: "All types" },
                { value: "new", label: "New" },
                { value: "returning", label: "Returning" },
              ]}
            />
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={sort ? `${sort.key}_${sort.direction}` : "totalSpent_desc"}
              onChange={value => setSort(parseKeyDirection<CustomerSortKey>(value))}
              options={OMNICHANNEL_CUSTOMER_SORT_OPTIONS}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "name"))}>
                    Name
                  </DashboardDataTableSortButton>
                </th>
                <th>Source</th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "type"))}>
                    Type
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalOrders"))}>
                    Orders
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalSpent"))}>
                    Total Spent
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "cltv"))}>
                    CLTV
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "lastOrder"))}>
                    Last Order
                  </DashboardDataTableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(customer => (
                <tr key={customer.id}>
                  <td>{customer.name}</td>
                  <td><SourceBadge source={customer.source} /></td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${customer.type}`}>{customer.type}</span></td>
                  <td className={dt.number}>{customer.totalOrders}</td>
                  <td className={dt.number}>{formatCurrency(customer.totalSpent)}</td>
                  <td className={dt.number}>{formatCurrency(customer.cltv)}</td>
                  <td>{customer.lastOrder}</td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const FinancialsSection: FC<{
  orders: OmnichannelOrder[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, dateRange, onDateRangeChange }) => {
  const summary = useMemo(() => getFinancialSummary(orders), [orders]);
  const monthly = useMemo(() => getMonthlyFinancials(orders), [orders]);
  const channelData = useMemo(() => getChannelDistribution(orders), [orders]);
  const taxBreakdown = useMemo(() => getTaxBreakdown(summary.taxes), [summary.taxes]);
  const margin = summary.revenue
    ? Math.round((summary.netProfit / summary.revenue) * 100)
    : 0;

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title="Financials Overview"
        subtitle="Analyze omnichannel revenue, profit, taxes, and refund performance."
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Revenue"
          value={formatCurrency(summary.revenue)}
          changePercent={12.5}
          tone="positive"
          icon={getDashboardKpiLucideIconComponent("Revenue")}
        />
        <MetricCard
          label="Net Profit"
          value={formatCurrency(summary.netProfit)}
          statusHint={`${margin}% margin`}
          tone={summary.netProfit >= 0 ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Net Profit")}
        />
        <MetricCard
          label="Total Taxes"
          value={formatCurrency(summary.taxes)}
          icon={getDashboardKpiLucideIconComponent("Total Taxes")}
        />
        <MetricCard
          label="Refunds"
          value={formatCurrency(summary.refunds)}
          changePercent={-3.2}
          tone="negative"
          icon={getDashboardKpiLucideIconComponent("Refunds")}
        />
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">Monthly P&amp;L</h3>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={value => `PKR ${Math.round(value / 1000)}k`} />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="revenue" name="Revenue" fill="#3569db" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#e0534d" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#2dbb5f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="sales-product-analytics-dashboard__chart-legend">
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--revenue">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>Revenue</span>
          </span>
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--expenses">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>Expenses</span>
          </span>
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--profit">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>Profit</span>
          </span>
        </div>
      </section>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">Revenue by Channel</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="label" innerRadius={42} outerRadius={76}>
                  {channelData.map((item, index) => (
                    <Cell
                  key={item.label}
                  fill={index === 0 ? "#3569db" : "#27c45a"}
                />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {channelData.map((item, index) => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{
                      backgroundColor: index === 0 ? "#3569db" : "#27c45a",
                    }}
                  />
                  <span>{item.label}</span>
                </span>
                <span>{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">Tax Breakdown</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={taxBreakdown} dataKey="value" nameKey="label" innerRadius={42} outerRadius={76}>
                  {taxBreakdown.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#f8a208" : index === 1 ? "#9b5785" : "#9ca8bd"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {taxBreakdown.map(item => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{
                      backgroundColor:
                        item.label === "Sales Tax"
                          ? "#f8a208"
                          : item.label === "VAT"
                            ? "#9b5785"
                            : "#9ca8bd",
                    }}
                  />
                  <span>{item.label}</span>
                </span>
                <span>{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">Financial Breakdown</h3>
        <div className="sales-product-analytics-dashboard__financial-bars">
            {[
              { label: "Gross Revenue", value: summary.revenue, ratio: 100 },
              { label: "Cost of Goods Sold", value: -summary.cogs, ratio: (summary.cogs / Math.max(summary.revenue, 1)) * 100 },
              { label: "Gross Profit", value: summary.grossProfit, ratio: (summary.grossProfit / Math.max(summary.revenue, 1)) * 100 },
              { label: "Operating Expenses", value: -summary.expenses, ratio: (summary.expenses / Math.max(summary.revenue, 1)) * 100 },
              { label: "Taxes", value: -summary.taxes, ratio: (summary.taxes / Math.max(summary.revenue, 1)) * 100 },
              { label: "Refunds", value: -summary.refunds, ratio: (summary.refunds / Math.max(summary.revenue, 1)) * 100 },
              { label: "Net Profit", value: summary.netProfit, ratio: (Math.abs(summary.netProfit) / Math.max(summary.revenue, 1)) * 100 },
            ].map(item => (
            <div key={item.label}>
              <div className="sales-product-analytics-dashboard__financial-bar-copy">
                <span>{item.label}</span>
                <span>{item.value < 0 ? "-" : ""}{formatCurrency(Math.abs(item.value))}</span>
              </div>
              <div className="sales-product-analytics-dashboard__financial-bar-track">
                <div
                  className={`sales-product-analytics-dashboard__financial-bar-fill ${item.value < 0 ? "sales-product-analytics-dashboard__financial-bar-fill--negative" : ""}`}
                  style={{ width: `${Math.min(Math.abs(item.ratio), 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const OmnichannelAnalyticsTabsDashboard: FC = () => {
  const [activeTab, setActiveTab] = useLocationTab(OMNICHANNEL_TAB_IDS, "overview");
  const [dateRange, setDateRange] = useState(() => {
    const end = dayjs();
    const start = end.subtract(29, "day");
    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    };
  });
  const orders = useMemo(() => generateOmnichannelOrders(), []);
  const products = useMemo(() => generateOmnichannelProducts(), []);
  const customers = useMemo(() => generateOmnichannelCustomers(), []);

  return (
    <div className="sales-product-analytics-dashboard">
      <div className="sales-product-analytics-dashboard__tabs">
        {OMNICHANNEL_TAB_ITEMS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`sales-product-analytics-dashboard__tab ${activeTab === tab.id ? "sales-product-analytics-dashboard__tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <OverviewSection
          orders={orders}
          customers={customers}
          products={products}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ) : null}
      {activeTab === "sales" ? (
        <SalesSection
          orders={orders}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
      {activeTab === "inventory" ? (
        <InventorySection
          products={products}
        />
      ) : null}
      {activeTab === "customers" ? (
        <CustomersSection
          customers={customers}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
      {activeTab === "financials" ? (
        <FinancialsSection
          orders={orders}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
    </div>
  );
};

export default OmnichannelAnalyticsTabsDashboard;
